import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCustomizationStore } from '../store/customizationStore';
import { useLocalizationStore } from '../store/localizationStore';
import { useCustomization } from '../hooks/useCustomization';
import { formatCurrency } from '../utils/units';
import { EnhancedTablePreview } from './3d/EnhancedTablePreview';
import { MaterialDialog } from './MaterialDialog';
import { OrderSummary } from './OrderSummary';
import { surfaceShapes, edgeProfiles, premiumServices, serviceCategories } from '../data/customization';
import { materials } from '../data/materials';
import { ServiceCategoryGroup } from './services/ServiceCategoryGroup';
import { cn } from '../lib/utils';
import { 
  Star, 
  Shield, 
  Clock, 
  MessageCircle, 
  X, 
  Info, 
  ArrowRight, 
  SunMoon, 
  Check,
  Box as Box3D,
  Grid,
  Eye,
  Calendar,
  RotateCcw,
  ZoomIn,
  ZoomOut,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Ruler,
  Palette,
  Settings2,
  PenTool,
  Truck,
  Wrench,
  Heart,
  Sparkles,
  Maximize2,
  Minimize2,
  Move3d,
  Layers,
  Zap,
  AlertCircle,
  Mail,
  Phone,
  Plus,
  Minus
} from 'lucide-react';

const viewModes = [
  { id: '3d', icon: Move3d, label: '3D View' },
  { id: 'top', icon: Grid, label: 'Top View' },
  { id: 'front', icon: Eye, label: 'Front View' }
] as const;

const materialPresets = [
  { id: 'glossy', name: 'Glossy', icon: SunMoon },
  { id: 'matte', name: 'Matte', icon: SunMoon }
] as const;

const sections = [
  { 
    id: 'material',
    name: 'Material Selection',
    icon: Palette,
    description: 'Choose your perfect surface material'
  },
  { 
    id: 'dimensions',
    name: 'Dimensions & Shape',
    icon: Ruler,
    description: 'Set the exact measurements and shape'
  },
  { 
    id: 'edge',
    name: 'Edge Profile',
    icon: PenTool,
    description: 'Select your preferred edge finish'
  },
  { 
    id: 'services',
    name: 'Premium Services',
    icon: Truck,
    description: 'Add professional services'
  }
] as const;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  },
  exit: { opacity: 0 }
};

const sectionVariants = {
  hidden: { 
    opacity: 0,
    y: 20,
    scale: 0.95
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30
    }
  }
};

const VAT_RATE = 0.21; // 21% VAT

export const Customize: React.FC = () => {
  const { 
    customizationOptions, 
    selectedMaterial,
    setStep,
    setCustomizationOptions,
    setMaterial
  } = useCustomizationStore();
  
  const { currency, language } = useLocalizationStore();
  const { handleDimensionChange, currentConstraints } = useCustomization();

  const [activeViewMode, setActiveViewMode] = useState<typeof viewModes[number]['id']>('3d');
  const [activeFinish, setActiveFinish] = useState<typeof materialPresets[number]['id']>('glossy');
  const [selectedServices, setSelectedServices] = useState<string[]>(customizationOptions?.selectedServices || []);
  const [showTooltip, setShowTooltip] = useState<string | null>(null);
  const [showMaterialDialog, setShowMaterialDialog] = useState(false);
  const [activeSection, setActiveSection] = useState<typeof sections[number]['id']>('material');
  const [showEdgeDetails, setShowEdgeDetails] = useState<string | null>(null);
  const [showShapeDetails, setShowShapeDetails] = useState<string | null>(null);
  const [showRequestModal, setShowRequestModal] = useState(false);

  const handleFinishChange = (finish: 'glossy' | 'matte') => {
    setActiveFinish(finish);
    if (selectedMaterial) {
      setMaterial({
        ...selectedMaterial,
        selectedFinish: finish
      });
    }
  };

  const handleServiceToggle = useMemo(() => (serviceId: string) => {
    const newServices = selectedServices.includes(serviceId)
      ? selectedServices.filter(id => id !== serviceId)
      : [...selectedServices, serviceId];
    
    setSelectedServices(newServices);
    
    if (customizationOptions) {
      setCustomizationOptions({
        ...customizationOptions,
        selectedServices: newServices
      });
    }
  }, [selectedServices, customizationOptions, setCustomizationOptions]);

  const handleEdgeProfileChange = useMemo(() => (profile: string) => {
    if (customizationOptions) {
      setCustomizationOptions({
        ...customizationOptions,
        edgeProfile: profile as any
      });
    }
  }, [customizationOptions, setCustomizationOptions]);

  const handleShapeChange = useMemo(() => (shape: string) => {
    if (customizationOptions) {
      const defaultDims = currentConstraints;
      setCustomizationOptions({
        ...customizationOptions,
        shape: shape as any,
        width: defaultDims.width.min,
        length: shape === 'round' ? defaultDims.width.min : defaultDims.length.min
      });
    }
  }, [customizationOptions, currentConstraints, setCustomizationOptions]);

  const handleThicknessSelect = (thickness: number) => {
    if (thickness === 20) {
      setShowRequestModal(true);
      return;
    }
    if (customizationOptions) {
      setCustomizationOptions({
        ...customizationOptions,
        thickness
      });
    }
  };

  const handleContinue = () => {
    setStep('inquiry');
  };

  const handleMaterialSelect = (material: Material) => {
    setMaterial(material);
    setShowMaterialDialog(false);
  };

  if (!customizationOptions || !selectedMaterial) return null;

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="min-h-screen bg-cream py-8 sm:py-16"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="hidden lg:block mb-8">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-gold/10 text-gold px-6 py-3 rounded-full mb-4"
            >
              <Sparkles className="w-5 h-5" />
              <span className="font-medium">Premium Design Studio</span>
            </motion.div>
            <motion.h1 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-4xl font-bold text-gold-dark mb-2"
            >
              Customize Your Perfect Surface
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-lg text-black/70 max-w-2xl mx-auto"
            >
              Fine-tune every detail of your design with our professional tools
            </motion.p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-9 space-y-6">
            <EnhancedTablePreview
              options={customizationOptions}
              materialTexture={selectedMaterial.image}
              initialFinish={activeFinish}
              compact={true}
            />

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              {sections.map((section) => {
                const Icon = section.icon;
                const isActive = activeSection === section.id;

                return (
                  <motion.button
                    key={section.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setActiveSection(section.id)}
                    className={`p-3 sm:p-4 rounded-xl text-left transition-colors ${
                      isActive 
                        ? 'bg-gold text-white'
                        : 'bg-white hover:bg-gold/5'
                    }`}
                  >
                    <div className={`p-2 rounded-lg inline-block mb-2 ${
                      isActive ? 'bg-white/20' : 'bg-gold/10'
                    }`}>
                      <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${isActive ? 'text-white' : 'text-gold'}`} />
                    </div>
                    <h3 className={`font-medium text-sm sm:text-base ${isActive ? 'text-white' : 'text-gold-dark'}`}>
                      {section.name}
                    </h3>
                    <p className={`text-xs sm:text-sm ${isActive ? 'text-white/80' : 'text-black/60'}`}>
                      {section.description}
                    </p>
                  </motion.button>
                );
              })}
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <AnimatePresence mode="wait">
                {activeSection === 'material' && (
                  <motion.div
                    key="material"
                    variants={sectionVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                      <div>
                        <h3 className="text-xl sm:text-2xl font-semibold text-gold-dark mb-2">
                          Selected Material
                        </h3>
                        <p className="text-sm sm:text-base text-black/70">
                          Choose the perfect material for your surface
                        </p>
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setShowMaterialDialog(true)}
                        className="w-full sm:w-auto px-4 sm:px-6 py-3 bg-gold text-white rounded-xl font-medium hover:bg-gold-dark transition-colors flex items-center justify-center gap-2"
                      >
                        <Palette className="w-5 h-5" />
                        Change Material
                      </motion.button>
                    </div>

                    <div className="bg-cream rounded-xl p-6">
                      <div className="flex flex-col sm:flex-row gap-6">
                        <img
                          src={selectedMaterial.image}
                          alt={selectedMaterial.name}
                          className="w-full sm:w-48 h-48 object-cover rounded-lg"
                        />
                        <div className="flex-1">
                          <h4 className="text-lg sm:text-xl font-semibold text-gold-dark mb-2">
                            {selectedMaterial.name}
                          </h4>
                          <p className="text-sm sm:text-base text-black/70 mb-4">
                            {selectedMaterial.description}
                          </p>

                          <div className="mb-6">
                            <h5 className="text-sm font-medium text-gold-dark mb-3">Surface Finish</h5>
                            <div className="flex gap-3">
                              {materialPresets.map(preset => (
                                <motion.button
                                  key={preset.id}
                                  whileHover={{ scale: 1.02 }}
                                  whileTap={{ scale: 0.98 }}
                                  onClick={() => handleFinishChange(preset.id)}
                                  className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                                    activeFinish === preset.id
                                      ? 'bg-gold text-white'
                                      : 'bg-white text-gold hover:bg-gold/5'
                                  }`}
                                >
                                  <preset.icon className="w-4 h-4" />
                                  <span className="font-medium">{preset.name}</span>
                                </motion.button>
                              ))}
                            </div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                            <div>
                              <span className="text-sm text-black/60">Price per slab</span>
                              <p className="text-xl sm:text-2xl font-bold text-gold">
                                {formatCurrency(selectedMaterial.price, currency, language)}
                              </p>
                            </div>
                            <div>
                              <span className="text-sm text-black/60">Available Stock</span>
                              <p className="text-xl sm:text-2xl font-bold text-gold">
                                {selectedMaterial.stock} m²
                              </p>
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-4 sm:gap-6">
                            <div className="flex items-center gap-2">
                              <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-gold" />
                              <span className="text-sm sm:text-base text-black/70">10-year warranty</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Star className="w-4 h-4 sm:w-5 sm:h-5 text-gold" />
                              <span className="text-sm sm:text-base text-black/70">
                                {selectedMaterial.popularity}% satisfaction
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-gold" />
                              <span className="text-sm sm:text-base text-black/70">Premium grade</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {activeSection === 'dimensions' && (
                  <motion.div
                    key="dimensions"
                    variants={sectionVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    className="space-y-8"
                  >
                    <div>
                      <h3 className="text-2xl font-semibold text-gold-dark mb-6">Surface Shape</h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {surfaceShapes.map((shape) => {
                          const Icon = shape.icon;
                          const isSelected = customizationOptions.shape === shape.id;
                          
                          return (
                            <motion.div
                              key={shape.id}
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={() => handleShapeChange(shape.id)}
                              className={`relative p-4 rounded-xl cursor-pointer ${
                                isSelected 
                                  ? 'bg-gold/10 border-2 border-gold' 
                                  : 'bg-white/50 border-2 border-transparent hover:border-gold/20'
                              }`}
                            >
                              <div className="flex items-center gap-4">
                                <div className={`w-12 h-12 flex items-center justify-center rounded-lg ${
                                  isSelected ? 'bg-gold text-white' : 'bg-gold/10 text-gold'
                                }`}>
                                  <Icon className="w-6 h-6" />
                                </div>
                                <div>
                                  <h4 className="font-medium text-gold-dark">{shape.name}</h4>
                                  <p className="text-sm text-black/70">{shape.description}</p>
                                </div>
                                {isSelected && (
                                  <Check className="w-5 h-5 text-gold ml-auto" />
                                )}
                              </div>

                              {shape.recommendedFor && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowShapeDetails(showShapeDetails === shape.id ? null : shape.id);
                                  }}
                                  className="absolute top-2 right-2 p-1 hover:bg-gold/5 rounded-lg text-gold"
                                >
                                  <Info className="w-4 h-4" />
                                </button>
                              )}

                              <AnimatePresence>
                                {showShapeDetails === shape.id && (
                                  <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="mt-4 pt-4 border-t border-gold/10"
                                  >
                                    <div className="space-y-3">
                                      <div>
                                        <h5 className="text-sm font-medium text-gold-dark mb-1">
                                          Details
                                        </h5>
                                        <p className="text-sm text-black/70">
                                          {shape.details}
                                        </p>
                                      </div>
                                      {shape.recommendedFor && (
                                        <div>
                                          <h5 className="text-sm font-medium text-gold-dark mb-1">
                                            Recommended For
                                          </h5>
                                          <div className="flex flex-wrap gap-2">
                                            {shape.recommendedFor?.map(rec => (
                                              <span
                                                key={rec}
                                                className="px-2 py-1 bg-gold/10 text-gold rounded-lg text-xs"
                                              >
                                                {rec}
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </motion.div>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-2xl font-semibold text-gold-dark mb-6">Dimensions</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {['width', 'length'].map((dim) => {
                          const isDisabled = customizationOptions.shape === 'round' && dim === 'length';
                          const constraints = currentConstraints[dim];
                          
                          return (
                            <div key={dim} className={cn(
                              "bg-white rounded-xl p-6 border border-gold/10 shadow-sm transition-opacity duration-200",
                              isDisabled ? "opacity-50" : "hover:border-gold/30"
                            )}>
                              <div className="flex items-center justify-between mb-4">
                                <label className="text-sm font-medium text-gold-dark capitalize flex items-center gap-2">
                                  {dim}
                                  <div className="relative group">
                                    <Info 
                                      className="w-4 h-4 text-gold/40 cursor-help"
                                      title={`Min: ${constraints.min}mm, Max: ${constraints.max}mm`}
                                    />
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 bg-white rounded-lg p-3 shadow-xl border border-gold/10 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200">
                                      <p className="text-xs text-black/70">
                                        Min: {constraints.min}mm<br />
                                        Max: {constraints.max}mm
                                      </p>
                                    </div>
                                  </div>
                                </label>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-gold/60">
                                    {Math.round(customizationOptions[dim] / 10)} cm
                                  </span>
                                </div>
                              </div>

                              <div className="relative mb-6">
                                <input
                                  type="number"
                                  value={customizationOptions[dim]}
                                  onChange={(e) => {
                                    const value = parseInt(e.target.value);
                                    if (!isNaN(value)) {
                                      handleDimensionChange(dim as any, value);
                                    }
                                  }}
                                  min={constraints.min}
                                  max={constraints.max}
                                  step={10}
                                  disabled={isDisabled}
                                  className={cn(
                                    "w-full px-4 py-3 bg-white border rounded-lg text-gold-dark focus:ring-2 focus:ring-gold focus:border-transparent transition-all duration-200",
                                    isDisabled 
                                      ? "border-gold/10 cursor-not-allowed"
                                      : "border-gold/20 hover:border-gold/40"
                                  )}
                                />
                                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                                  <span className="text-black/40 text-sm">mm</span>
                                </div>
                              </div>

                              <div className="space-y-2">
                                <div className="relative">
                                  <div className="absolute -top-2 left-0 right-0 h-8 bg-gradient-to-b from-white to-transparent pointer-events-none" />
                                  <input
                                    type="range"
                                    value={customizationOptions[dim]}
                                    onChange={(e) => handleDimensionChange(dim as any, parseInt(e.target.value))}
                                    min={constraints.min}
                                    max={constraints.max}
                                    step={10}
                                    disabled={isDisabled}
                                    className={cn(
                                      "w-full h-2 appearance-none bg-gradient-to-r from-gold/20 to-gold rounded-full cursor-pointer",
                                      "focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2",
                                      "[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:h-6",
                                      "[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white",
                                      "[&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-gold/40",
                                      "[&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:cursor-pointer",
                                      "[&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:duration-200",
                                      "[&::-webkit-slider-thumb]:hover:scale-110 [&::-webkit-slider-thumb]:hover:border-gold",
                                      "disabled:opacity-50 disabled:cursor-not-allowed"
                                    )}
                                  />
                                  <div className="absolute -bottom-2 left-0 right-0 h-8 bg-gradient-to-t from-white to-transparent pointer-events-none" />
                                </div>
                                <div className="flex justify-between px-1">
                                  <span className="text-xs text-gold/40">
                                    {constraints.min}mm
                                  </span>
                                  <span className="text-xs text-gold/40">
                                    {constraints.max}mm
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}

                        <div className="md:col-span-2">
                          <div className="bg-white rounded-xl p-6 border border-gold/10 shadow-sm">
                            <label className="text-sm font-medium text-gold-dark mb-4 flex items-center gap-2">
                              Thickness
                              <div className="relative group">
                                <Info 
                                  className="w-4 h-4 text-gold/40 cursor-help"
                                />
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 bg-white rounded-lg p-3 shadow-xl border border-gold/10 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200">
                                  <p className="text-xs text-black/70">
                                    Choose between standard (12mm) and premium (20mm) thickness options.
                                  </p>
                                </div>
                              </div>
                            </label>
                            <div className="grid grid-cols-2 gap-4">
                              {[12, 20].map((thickness) => {
                                const isSelected = customizationOptions.thickness === thickness;
                                const isUponRequest = thickness === 20;

                                return (
                                  <motion.button
                                    key={thickness}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={() => handleThicknessSelect(thickness)}
                                    className={cn(
                                      "relative p-4 rounded-xl transition-all duration-200",
                                      isSelected 
                                        ? "bg-gold text-white shadow-lg"
                                        : "bg-cream text-gold-dark hover:bg-gold/5"
                                    )}
                                  >
                                    <span className="block text-lg font-semibold">
                                      {thickness}mm
                                    </span>
                                    <span className="text-sm opacity-80">
                                      {thickness === 12 ? 'Standard' : 'Premium'}
                                    </span>
                                    {isUponRequest && (
                                      <span className="absolute top-2 right-2 bg-gold/20 text-gold text-xs px-2 py-1 rounded-full">
                                        Upon Request
                                      </span>
                                    )}
                                  </motion.button>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {activeSection === 'edge' && (
                  <motion.div
                    key="edge"
                    variants={sectionVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                  >
                    <h3 className="text-2xl font-semibold text-gold-dark mb-6">Edge Profile</h3>
                    <div className="space-y-8">
                      {edgeProfiles.map((category) => (
                        <div key={category.category}>
                          <h4 className="font-medium text-gold-dark mb-2">{category.category}</h4>
                          <p className="text-sm text-black/70 mb-4">{category.description}</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {category.profiles.map((profile) => {
                              const isSelected = customizationOptions.edgeProfile === profile.id;
                              
                              return (
                                <motion.div
                                  key={profile.id}
                                  whileHover={{ scale: 1.02 }}
                                  whileTap={{ scale: 0.98 }}
                                  onClick={() => handleEdgeProfileChange(profile.id)}
                                  className={`relative p-4 rounded-xl cursor-pointer border-2 transition-colors ${
                                    isSelected
                                      ? 'bg-gold/10 border-gold'
                                      : 'bg-white hover:bg-gold/5 border-transparent hover:border-gold/20'
                                  }`}
                                >
                                  <div className="flex items-center gap-4">
                                    <div className={`w-12 h-12 flex items-center justify-center rounded-lg ${
                                      isSelected ? 'bg-gold text-white' : 'bg-gold/10 text-gold'
                                    }`}>
                                      <span className="text-2xl font-mono">{profile.icon}</span>
                                    </div>
                                    <div>
                                      <h5 className="font-medium text-gold-dark mb-1">{profile.name}</h5>
                                      <p className="text-sm text-black/70">{profile.description}</p>
                                      {profile.price > 0 && (
                                        <span className="text-sm text-gold mt-2 block">
                                          +{formatCurrency(profile.price, currency, language)} per linear meter
                                        </span>
                                      )}
                                    </div>
                
                                    {isSelected && (
                                      <Check className="w-5 h-5 text-gold ml-auto" />
                                    )}
                                  </div>

                                  {profile.details && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setShowEdgeDetails(showEdgeDetails === profile.id ? null : profile.id);
                                      }}
                                      className="absolute top-2 right-2 p-1 hover:bg-gold/5 rounded-lg text-gold"
                                    >
                                      <Info className="w-4 h-4" />
                                    </button>
                                  )}

                                  <AnimatePresence>
                                    {showEdgeDetails === profile.id && (
                                      <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="mt-4 pt-4 border-t border-gold/10"
                                      >
                                        <div className="space-y-3">
                                          <div>
                                            <h5 className="text-sm font-medium text-gold-dark mb-1">
                                              Details
                                            </h5>
                                            <p className="text-sm text-black/70">
                                              {profile.details}
                                            </p>
                                          </div>
                                          {profile.recommendedFor && (
                                            <div>
                                              <h5 className="text-sm font-medium text-gold-dark mb-1">
                                                Recommended For
                                              </h5>
                                              <div className="flex flex-wrap gap-2">
                                                {profile.recommendedFor.map(rec => (
                                                  <span
                                                    key={rec}
                                                    className="px-2 py-1 bg-gold/10 text-gold rounded-lg text-xs"
                                                  >
                                                    {rec}
                                                  </span>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </motion.div>
                                    )}
                                  </AnimatePresence>
                                </motion.div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}

                {activeSection === 'services' && (
                  <motion.div
                    key="services"
                    variants={sectionVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                      <div>
                        <h3 className="text-xl sm:text-2xl font-semibold text-gold-dark mb-2">
                          Premium Services
                        </h3>
                        <p className="text-sm sm:text-base text-black/70">
                          Enhance your experience with our professional services
                        </p>
                      </div>
                    </div>

                    <div className="space-y-8">
                      {serviceCategories.map((category) => (
                        <ServiceCategoryGroup
                          key={category.id}
                          category={category}
                          selectedServices={selectedServices}
                          onToggle={handleServiceToggle}
                          currency={currency}
                          language={language}
                        />
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="sticky top-8">
              <OrderSummary
                options={customizationOptions}
                material={selectedMaterial}
                selectedServices={selectedServices}
                onContinue={handleContinue}
                currency={currency}
                language={language}
              />
            </div>
          </div>
        </div>
      </div>

      <MaterialDialog
        show={showMaterialDialog}
        onClose={() => setShowMaterialDialog(false)}
        onSelect={handleMaterialSelect}
        materials={materials}
        selectedMaterial={selectedMaterial}
      />

      {/* Request Modal */}
      <AnimatePresence>
        {showRequestModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={() => setShowRequestModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl p-6 max-w-md mx-4 relative"
              onClick={e => e.stopPropagation()}
            >
              <button
                onClick={() => setShowRequestModal(false)}
                className="absolute top-4 right-4 text-black/40 hover:text-black/60"
              >
                <X className="w-5 h-5" />
              </button>
              
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-gold/10 rounded-lg">
                  <Info className="w-6 h-6 text-gold" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">
                  20mm Thickness Available Upon Request
                </h3>
              </div>
              
              <p className="text-gray-600 mb-6">
                The 20mm thickness option requires special ordering. Our team will be happy to assist you with availability and lead times.
              </p>

              <div className="space-y-4 mb-6">
                <a
                  href="tel:+18001234567"
                  className="flex items-center gap-3 p-3 bg-gold/5 rounded-lg hover:bg-gold/10 transition-colors"
                >
                  <Phone className="w-5 h-5 text-gold" />
                  <div>
                    <p className="font-medium text-gold-dark">Call Us</p>
                    <p className="text-sm text-black/60">+1 (800) 123-4567</p>
                  </div>
                </a>

                <a
                  href="mailto:sales@example.com"
                  className="flex items-center gap-3 p-3 bg-gold/5 rounded-lg hover:bg-gold/10 transition-colors"
                >
                  <Mail className="w-5 h-5 text-gold" />
                  <div>
                    <p className="font-medium text-gold-dark">Email Us</p>
                    <p className="text-sm text-black/60">sales@example.com</p>
                  </div>
                </a>
              </div>

              <div className="flex flex-col gap-3">
                <button
                  onClick={() => {
                    if (customizationOptions) {
                      setCustomizationOptions({
                        ...customizationOptions,
                        thickness: 20
                      });
                    }
                    setShowRequestModal(false);
                  }}
                  className="w-full px-4 py-3 bg-gold text-white rounded-lg hover:bg-gold-dark font-medium"
                >
                  Still select 20mm
                </button>

                <button
                  onClick={() => {
                    if (customizationOptions) {
                      setCustomizationOptions({
                        ...customizationOptions,
                        thickness: 12
                      });
                    }
                    setShowRequestModal(false);
                  }}
                  className="w-full px-4 py-3 bg-white text-gold border-2 border-gold rounded-lg hover:bg-gold/5 font-medium"
                >
                  Select 12mm Instead
                </button>

                <button
                  onClick={() => setShowRequestModal(false)}
                  className="w-full px-4 py-3 text-gray-600 hover:text-gray-800"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};